/**
 * FC Hold value/comment API calls (FC Hold orgs, e.g. OCSRI Medical PA orders).
 * @module fcHold
 */

import { FcHoldStateResponse } from "../../data-model/fcHoldState";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { throwApiError } from "../throwApiError";
import { getBearerToken } from "./bearerToken";

/**
 * Stamps an FC Hold order as picked for processing (adds the
 * `RISA Working (DoP)` comment and the required `Auth Initiated` value).
 * Idempotent: an existing FC Hold comment is preserved.
 *
 * @param orderId - Medical order identifier.
 * @param orgId - Organisation the order belongs to.
 * @param signal - Optional AbortSignal.
 * @returns The current FC Hold state.
 * @throws {Error} On HTTP/network failure.
 */
export const fetchFcHoldOnPick = async (
  orderId: string,
  orgId?: string,
  signal?: AbortSignal,
): Promise<FcHoldStateResponse> => {
  try {
    const token = await getBearerToken();
    const query = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";
    const response = await fetch(
      `${API_ENDPOINTS.FC_HOLD_ON_PICK(orderId)}${query}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        ...(signal && { signal }),
      },
    );
    if (!response.ok) {
      await throwApiError(response);
    }
    return (await response.json()) as FcHoldStateResponse;
  } catch (error) {
    console.error("Failed to fetch FC Hold on pick:", error);
    throw error;
  }
};

/**
 * Persists the operator-edited FC Hold comment (the EMR Comment box content).
 *
 * @param orderId - Medical order identifier.
 * @param orgId - Organisation the order belongs to.
 * @param comment - Edited comment text.
 * @param signal - Optional AbortSignal.
 * @returns The current FC Hold state.
 * @throws {Error} On HTTP/network failure.
 */
export const saveFcHoldComment = async (
  orderId: string,
  comment: string,
  orgId?: string,
  signal?: AbortSignal,
): Promise<FcHoldStateResponse> => {
  try {
    const token = await getBearerToken();
    const query = orgId ? `?org_id=${encodeURIComponent(orgId)}` : "";
    const response = await fetch(
      `${API_ENDPOINTS.FC_HOLD_UPDATE(orderId)}${query}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          order_id: orderId,
          org_id: orgId ?? null,
          comment,
        }),
        ...(signal ? { signal } : {}),
      },
    );
    if (!response.ok) {
      await throwApiError(response);
    }
    return (await response.json()) as FcHoldStateResponse;
  } catch (error) {
    console.error("Failed to save FC Hold comment:", error);
    throw error;
  }
};
